import React, { useState, useEffect } from 'react';
import { investmentsAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { 
  TrendingUp,
  Clock,
  AlertCircle,
  DollarSign,
  Percent,
  Calendar,
  X,
  Zap,
  Crown,
  Star,
  Lock,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

const planIcons = {
  basic: Zap,
  premium: Star,
  vip: Crown
};

const planColors = {
  basic: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', glow: 'shadow-cyan-500/20' },
  premium: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', glow: 'shadow-yellow-500/20' },
  vip: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', glow: 'shadow-purple-500/20' }
};

export default function InvestmentsPage() {
  const { user, refreshUser } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amount, setAmount] = useState('');
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [investmentsRes, plansRes] = await Promise.all([
        investmentsAPI.getAll(),
        investmentsAPI.getPlans()
      ]);
      setInvestments(investmentsRes.data.investments);
      setPlans(plansRes.data.plans);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setAmount(plan.min_amount.toString());
  };

  const handleCreateTradingPosition = async () => {
    if (!selectedPlan) {
      toast.error('Please select a trading plan');
      return;
    }

    const tradeAmount = parseFloat(amount);
    if (!amount || tradeAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (tradeAmount < selectedPlan.min_amount) {
      toast.error(`Minimum for ${selectedPlan.name} is $${selectedPlan.min_amount}`);
      return;
    }

    if (tradeAmount > selectedPlan.max_amount) {
      toast.error(`Maximum for ${selectedPlan.name} is $${selectedPlan.max_amount}`);
      return;
    }

    if (tradeAmount > (user?.balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setCreating(true);
    try {
      await investmentsAPI.create({
        amount: tradeAmount,
        plan_id: selectedPlan.id
      });
      toast.success(`Trading position activated in ${selectedPlan.name}!`);
      setAmount('');
      setSelectedPlan(null);
      setDialogOpen(false);
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate trading position');
    } finally {
      setCreating(false);
    }
  };

  const totalInTrading = investments.reduce((acc, inv) => acc + inv.amount, 0);
  const totalProfit = investments.reduce((acc, inv) => acc + (inv.total_profit || 0), 0);
  const activePositions = investments.filter(inv => inv.status === 'active');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-4 sm:space-y-6"
      data-testid="investments-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white">Trading Plans</h1>
          <p className="text-white/60 mt-1 text-sm sm:text-base">Choose a plan to activate automated trading</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary touch-manipulation" data-testid="new-trading-btn">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              New Trading Position
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0f1a] border-white/10 max-w-lg mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
              <X className="h-4 w-4 text-white/70 hover:text-white" />
              <span className="sr-only">Close</span>
            </DialogClose>
            <DialogHeader>
              <DialogTitle className="text-white text-lg">Activate Trading</DialogTitle>
              <DialogDescription className="text-white/60 text-sm">
                Select a plan and enter your trading amount
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Balance Display */}
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-white/60 text-sm">Available Balance</span>
                  <span className="font-mono font-bold text-emerald-400">
                    ${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                  </span>
                </div>
              </div>

              {/* Plan Selection */}
              <div className="space-y-3">
                <Label className="text-white/80 text-sm">Select Trading Plan</Label>
                <div className="space-y-2">
                  {plans.map((plan) => {
                    const Icon = planIcons[plan.id] || TrendingUp;
                    const colors = planColors[plan.id] || planColors.basic;
                    const isSelected = selectedPlan?.id === plan.id;
                    
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full p-3 rounded-xl border transition-all text-left ${
                          isSelected
                            ? `${colors.bg} ${colors.border} ${colors.glow} shadow-lg`
                            : 'border-white/10 hover:border-white/30 bg-white/5'
                        }`}
                        data-testid={`plan-${plan.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center shrink-0`}>
                            <Icon className={`w-5 h-5 ${colors.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className={`font-semibold ${isSelected ? colors.text : 'text-white'}`}>
                                {plan.name}
                              </span>
                              <span className={`text-lg font-bold ${colors.text}`}>
                                {plan.daily_rate}%
                                <span className="text-xs text-white/50 ml-1">daily</span>
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1 text-xs text-white/50">
                              <span>${plan.min_amount.toLocaleString()} - ${plan.max_amount.toLocaleString()}</span>
                              <span>•</span>
                              <span>{plan.lock_days} days lock</span>
                            </div>
                          </div>
                          {isSelected && (
                            <CheckCircle className={`w-5 h-5 ${colors.text} shrink-0`} />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Amount Input */}
              {selectedPlan && (
                <div className="space-y-3">
                  <Label className="text-white/80 text-sm">Trading Amount (USD)</Label>
                  <Input
                    type="number"
                    placeholder={`Min: $${selectedPlan.min_amount}`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="input-field"
                    data-testid="investment-amount-input"
                  />
                  
                  {/* Quick Amount Buttons */}
                  <div className="grid grid-cols-4 gap-2">
                    {[selectedPlan.min_amount, Math.floor((selectedPlan.min_amount + selectedPlan.max_amount) / 4), Math.floor((selectedPlan.min_amount + selectedPlan.max_amount) / 2), Math.min(user?.balance || 0, selectedPlan.max_amount)].map((val, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setAmount(Math.floor(val).toString())}
                        className="p-2 rounded-lg border border-white/10 hover:border-cyan-500/50 transition-colors text-xs text-white/70"
                      >
                        {idx === 3 ? 'Max' : `$${val.toLocaleString()}`}
                      </button>
                    ))}
                  </div>

                  {/* Profit Calculator */}
                  {amount && parseFloat(amount) > 0 && (
                    <div className={`p-3 rounded-lg ${planColors[selectedPlan.id]?.bg || 'bg-emerald-500/10'} border ${planColors[selectedPlan.id]?.border || 'border-emerald-500/30'}`}>
                      <h4 className={`${planColors[selectedPlan.id]?.text || 'text-emerald-400'} font-medium mb-2 text-sm`}>
                        Estimated Returns ({selectedPlan.name})
                      </h4>
                      <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between">
                          <span className="text-white/60">Daily Profit ({selectedPlan.daily_rate}%)</span>
                          <span className="text-white font-mono">
                            +${(parseFloat(amount) * selectedPlan.daily_rate / 100).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/60">Lock Period ({selectedPlan.lock_days} days)</span>
                          <span className="text-white font-mono">
                            +${(parseFloat(amount) * selectedPlan.daily_rate / 100 * selectedPlan.lock_days).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between pt-1.5 border-t border-white/10">
                          <span className="text-white/60">Monthly Profit</span>
                          <span className={`${planColors[selectedPlan.id]?.text || 'text-emerald-400'} font-mono font-bold`}>
                            +${(parseFloat(amount) * selectedPlan.daily_rate / 100 * 30).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Lock Period Warning */}
              {selectedPlan && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-400/10 border border-yellow-400/30">
                  <Lock className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-400">
                    Your trading position will be locked for {selectedPlan.lock_days} days. You can withdraw after the lock period ends.
                  </p>
                </div>
              )}

              <Button
                type="button"
                onClick={handleCreateTradingPosition}
                className="btn-primary w-full touch-manipulation"
                disabled={creating || !selectedPlan || !amount || parseFloat(amount) <= 0}
                data-testid="confirm-trading-btn"
              >
                {creating ? 'Activating...' : `Start Trading with ${selectedPlan?.name || 'Selected Plan'}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Trading Plans Section */}
      <motion.div variants={fadeInUp}>
        <h2 className="text-lg sm:text-xl font-outfit font-semibold text-white mb-4">Trading Plans</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const Icon = planIcons[plan.id] || TrendingUp;
            const colors = planColors[plan.id] || planColors.basic;
            
            return (
              <div 
                key={plan.id}
                className={`glass-card p-5 ${colors.border} border hover:${colors.glow} hover:shadow-lg transition-all`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-white">{plan.name}</h3>
                    <p className={`text-2xl font-bold ${colors.text}`}>{plan.daily_rate}%<span className="text-sm text-white/50">/day</span></p>
                  </div>
                </div>
                <p className="text-white/60 text-sm mb-4">{plan.description}</p>
                <div className="space-y-2 mb-4">
                  {plan.features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className={`w-4 h-4 ${colors.text} shrink-0`} />
                      <span className="text-white/70">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-white/10">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Trading Range</span>
                    <span className="text-white font-mono">${plan.min_amount.toLocaleString()} - ${plan.max_amount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div variants={fadeInUp} className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="glass-card p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-white/60 text-[10px] sm:text-sm">Trading</p>
              <p className="text-sm sm:text-2xl font-mono font-bold text-white">
                ${totalInTrading.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </p>
            </div>
          </div>
        </div>
        <div className="glass-card p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-white/60 text-[10px] sm:text-sm">Profit</p>
              <p className="text-sm sm:text-2xl font-mono font-bold text-emerald-400">
                +${totalProfit.toLocaleString('en-US', { minimumFractionDigits: 0 })}
              </p>
            </div>
          </div>
        </div>
        <div className="glass-card p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center shrink-0">
              <Percent className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-white/60 text-[10px] sm:text-sm">Active</p>
              <p className="text-sm sm:text-2xl font-mono font-bold text-yellow-400">
                {activePositions.length}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Active Trading Positions */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-outfit font-semibold text-white mb-4 sm:mb-6">Active Trading Positions</h2>
        
        <div className="space-y-3 sm:space-y-4">
          {investments.map((investment) => {
            const daysActive = differenceInDays(new Date(), new Date(investment.created_at));
            const colors = planColors[investment.plan_id] || planColors.basic;
            const Icon = planIcons[investment.plan_id] || TrendingUp;
            const isLocked = investment.unlock_date && new Date(investment.unlock_date) > new Date();
            const daysUntilUnlock = investment.unlock_date ? differenceInDays(new Date(investment.unlock_date), new Date()) : 0;
            
            return (
              <div 
                key={investment.id}
                className={`p-4 rounded-xl bg-white/5 border ${colors.border} hover:${colors.glow} hover:shadow-md transition-all`}
              >
                {/* Mobile View */}
                <div className="sm:hidden">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.bg}`}>
                        <Icon className={`w-5 h-5 ${colors.text}`} />
                      </div>
                      <div>
                        <p className={`font-semibold ${colors.text}`}>{investment.plan_name || 'Basic Plan'}</p>
                        <p className="font-mono font-bold text-white">
                          ${investment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                    <span className={`status-badge ${
                      investment.status === 'active' ? 'status-badge-success' : 'status-badge-warning'
                    }`}>
                      {investment.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-white/10">
                    <div>
                      <p className="text-white/50 text-[10px]">Days</p>
                      <p className="text-white font-mono text-sm">{daysActive}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-[10px]">Rate</p>
                      <p className={`${colors.text} font-mono text-sm`}>{investment.daily_rate}%</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-[10px]">Profit</p>
                      <p className="text-emerald-400 font-mono text-sm font-bold">
                        +${(investment.total_profit || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  {isLocked && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-yellow-400">
                      <Lock className="w-3 h-3" />
                      <span>Unlocks in {daysUntilUnlock} days</span>
                    </div>
                  )}
                </div>

                {/* Desktop View */}
                <div className="hidden sm:flex flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colors.bg}`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div>
                      <p className={`font-semibold ${colors.text}`}>{investment.plan_name || 'Basic Plan'}</p>
                      <p className="font-mono font-bold text-white text-lg">
                        ${investment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-white/50 text-sm flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(investment.created_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-white/50 text-xs">Days Active</p>
                      <p className="text-white font-mono">{daysActive}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-white/50 text-xs">Daily Rate</p>
                      <p className={`${colors.text} font-mono`}>{investment.daily_rate}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-white/50 text-xs">Total Profit</p>
                      <p className="text-emerald-400 font-mono font-bold">
                        +${(investment.total_profit || 0).toFixed(2)}
                      </p>
                    </div>
                    {isLocked ? (
                      <div className="flex items-center gap-2 text-yellow-400 text-sm">
                        <Lock className="w-4 h-4" />
                        <span>{daysUntilUnlock}d</span>
                      </div>
                    ) : (
                      <span className={`status-badge ${
                        investment.status === 'active' ? 'status-badge-success' : 'status-badge-warning'
                      }`}>
                        {investment.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          {investments.length === 0 && (
            <div className="text-center py-8 sm:py-12">
              <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/40 mb-4 text-sm sm:text-base">No active trading positions</p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="btn-secondary touch-manipulation"
              >
                Start Your First Trade
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
